import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  InputLabel,
  FormControl
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

const TenantCreate = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    tenant_name: '',
    tenant_code: '',
    si_id: 'SI-HIOSAI-001', // Default SI ID
    description: '',
    active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'active' ? checked : value
    }));
  };

  // Generate tenant code based on name
  const generateTenantCode = () => {
    if (!formData.tenant_name) return;
    
    // Create a code based on tenant name and current date
    const namePart = formData.tenant_name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .substring(0, 20);
    
    const date = new Date();
    const datePart = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}`;
    
    const tenantCode = `${namePart}_${datePart}`;
    
    setFormData(prev => ({
      ...prev,
      tenant_code: tenantCode
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      // Validate form data
      if (!formData.tenant_name || !formData.tenant_code || !formData.si_id) {
        throw new Error('Please fill in all required fields');
      }
      
      // Submit to API
      const response = await axios.post('/api/v1/tenants', formData);
      
      setSuccess(true);
      
      // Redirect to tenant management after a short delay
      setTimeout(() => {
        navigate('/developers/tenant-management');
      }, 1500);
    } catch (err) {
      console.error('Failed to create tenant:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to create tenant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/developers/tenant-management')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          Create New Tenant
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Tenant created successfully!
        </Alert>
      )}
      
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Tenant Name *"
                name="tenant_name"
                value={formData.tenant_name}
                onChange={handleChange}
                onBlur={generateTenantCode}
                fullWidth
                required
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Tenant Code *"
                name="tenant_code"
                value={formData.tenant_code}
                onChange={handleChange}
                fullWidth
                required
                disabled={loading}
                helperText="Unique identifier for the tenant"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="si-id-label">System Integrator *</InputLabel>
                <Select
                  labelId="si-id-label"
                  name="si_id"
                  value={formData.si_id}
                  onChange={handleChange}
                  disabled={loading}
                  required
                >
                  <MenuItem value="SI-HIOSAI-001">HIOSAI (SI-HIOSAI-001)</MenuItem>
                  <MenuItem value="SI-PARTNER-001">Partner 1 (SI-PARTNER-001)</MenuItem>
                  <MenuItem value="SI-PARTNER-002">Partner 2 (SI-PARTNER-002)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    name="active"
                    checked={formData.active}
                    onChange={handleChange}
                    disabled={loading}
                  />
                }
                label="Active"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={4}
                disabled={loading}
              />
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              color="primary" 
              type="submit"
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Tenant'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default TenantCreate; 